/**
 * Attestation Dashboard — annual FDR compliance attestations, fully operable.
 *
 * Wires the previously-orphaned write endpoints:
 *   POST  /api/fdr/attestations       create one, or launch the year campaign (bulk)
 *   PATCH /api/fdr/attestations/:id   status transitions, attester, nine element flags, review
 *
 * Lifecycle: pending → sent → (reminder_sent) → received → under_review → accepted / rejected.
 * Delivery is external for now (Phase 2 brings the portal + e-sign); the record here is the
 * evidence trail. An element the entity would not confirm should become a finding.
 */
import React, { useState, useEffect, useCallback } from 'react';
import Attachments from './Attachments';
import QuestionBuilder from './QuestionBuilder';

const API = process.env.REACT_APP_API_URL;
const inputCls = 'w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400';
const btnPrimary = 'px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl text-sm font-semibold shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 transition-all disabled:opacity-50';

const STATUS_CFG = {
  pending:         { label: 'Not Sent',     color: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
  sent:            { label: 'Sent',         color: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  reminder_sent:   { label: 'Reminded',     color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  received:        { label: 'Received',     color: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  under_review:    { label: 'Reviewing',    color: 'bg-indigo-50 text-indigo-700', dot: 'bg-indigo-500' },
  accepted:        { label: 'Accepted',     color: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  rejected:        { label: 'Rejected',     color: 'bg-red-50 text-red-600', dot: 'bg-red-500' },
  overdue:         { label: 'Overdue',      color: 'bg-red-100 text-red-700', dot: 'bg-red-600' },
};

const ELEMENTS = [
  { key: 'attests_element_1', label: 'Policies' },
  { key: 'attests_element_2', label: 'Code of Conduct' },
  { key: 'attests_element_3', label: 'CO Designation' },
  { key: 'attests_element_4', label: 'Training' },
  { key: 'attests_element_5', label: 'Monitoring' },
  { key: 'attests_element_6', label: 'Reporting' },
  { key: 'attests_element_7', label: 'Enforcement' },
  { key: 'attests_exclusion_screening', label: 'LEIE/SAM' },
  { key: 'attests_hipaa', label: 'HIPAA' },
];

export default function AttestationDashboard({ onOpenEntity }) {
  const [attestations, setAttestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear().toString());
  const [statusFilter, setStatusFilter] = useState('');
  const [showLaunch, setShowLaunch] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(new Set());   // bulk-edit selection

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/api/fdr/attestations?`;
      if (yearFilter) url += `year=${yearFilter}&`;
      if (statusFilter) url += `status=${statusFilter}&`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAttestations(data.attestations || []);
    } catch {}
    setLoading(false);
  }, [yearFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const patchAttestation = async (id, body) => {
    await fetch(`${API}/api/fdr/attestations/${id}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).catch(() => {});
    load();
  };

  const total = attestations.length;
  const accepted = attestations.filter(a => a.status === 'accepted').length;
  const pending = attestations.filter(a => ['pending', 'sent', 'reminder_sent'].includes(a.status)).length;
  const completionPct = total > 0 ? Math.round((accepted / total) * 100) : 0;

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Annual Attestations</h2>
          <p className="text-sm text-slate-500 mt-0.5">CMS Chapter 21 compliance attestation tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20">
            {[2026, 2025, 2024].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20">
            <option value="">All statuses</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button onClick={() => setShowQuestions(true)}
            className="px-3 py-2 text-sm font-semibold rounded-xl border border-slate-300 text-slate-600 hover:bg-slate-50">
            Edit questions
          </button>
          <button onClick={() => setShowLaunch(true)} className={btnPrimary}>Launch Campaign</button>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
          <div className="text-sm text-slate-500">Total</div>
          <div className="text-2xl font-bold text-slate-800 mt-1">{total}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
          <div className="text-sm text-slate-500">Accepted</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1">{accepted}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
          <div className="text-sm text-slate-500">Pending</div>
          <div className="text-2xl font-bold text-amber-600 mt-1">{pending}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-5">
          <div className="text-sm text-slate-500">Completion</div>
          <div className="flex items-center gap-3 mt-1">
            <div className="text-2xl font-bold text-slate-800">{completionPct}%</div>
            <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${completionPct >= 90 ? 'bg-emerald-500' : completionPct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                style={{ width: `${completionPct}%` }} />
            </div>
          </div>
        </div>
      </div>

      {/* Attestation cards */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">Loading...</div>
        ) : attestations.length === 0 ? (
          <div className="p-12 text-center text-slate-400">No attestations for {yearFilter}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {attestations.map(a => {
              const s = STATUS_CFG[a.status] || STATUS_CFG.pending;
              const elementCount = ELEMENTS.filter(e => a[e.key]).length;

              const isExpanded = expandedId === a.id;
              return (
                <div key={a.id}>
                  <div onClick={(ev) => {
                      if (ev.shiftKey || ev.ctrlKey || ev.metaKey) {
                        setSelected(prev => { const n = new Set(prev); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; });
                        return;
                      }
                      setExpandedId(isExpanded ? null : a.id);
                    }}
                    className={`px-6 py-4 transition-colors cursor-pointer ${selected.has(a.id) ? 'bg-orange-50/60' : 'hover:bg-slate-50/30'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <input type="checkbox" checked={selected.has(a.id)}
                          onClick={ev => ev.stopPropagation()}
                          onChange={() => setSelected(prev => { const n = new Set(prev); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })}
                          className="rounded border-slate-300 text-orange-600 focus:ring-orange-500/30" />
                        <div className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                        <div>
                          <button onClick={(ev) => { ev.stopPropagation(); onOpenEntity(a.entity_id); }}
                            className="font-semibold text-slate-800 hover:text-orange-600 hover:underline text-left">
                            {a.entity_name}
                          </button>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {a.period_start?.split('T')[0]} to {a.period_end?.split('T')[0]}
                            {a.due_date && <span className="ml-2">Due: {a.due_date.split('T')[0]}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-0.5">
                          {ELEMENTS.map(e => (
                            <div key={e.key} title={e.label}
                              className={`w-5 h-5 rounded text-[9px] font-bold flex items-center justify-center ${
                                a[e.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-400'
                              }`}>
                              {a[e.key] ? (
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                                </svg>
                              ) : '-'}
                            </div>
                          ))}
                        </div>

                        <span className="text-xs text-slate-500">{elementCount}/{ELEMENTS.length}</span>
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.color}`}>{s.label}</span>
                      </div>
                    </div>

                    {a.attested_by_name && (
                      <div className="mt-2 text-xs text-slate-500 pl-5">
                        Attested by {a.attested_by_name} ({a.attested_by_title}) on {a.attested_at?.split('T')[0]}
                      </div>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="space-y-3 pb-4">
                      <AttestationWorkPanel attestation={a} onPatch={(body) => patchAttestation(a.id, body)} />
                      <div className="px-6">
                        <Attachments entityId={a.entity_id} recordKind="attestation" recordId={a.id}
                          category="attestation_evidence" title="Supporting documents" compact />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showLaunch && (
        <LaunchCampaignModal year={Number(yearFilter)} existing={attestations}
          onClose={() => setShowLaunch(false)}
          onDone={() => { setShowLaunch(false); load(); }} />
      )}

      {showQuestions && <QuestionBuilder onClose={() => setShowQuestions(false)} />}

      {/* Bulk-edit floating toolbar (Asana pattern): shift/ctrl-click or checkboxes to select */}
      {selected.size > 0 && (
        <BulkBar count={selected.size}
          onClear={() => setSelected(new Set())}
          onSetDue={async (date) => {
            await Promise.all([...selected].map(id => fetch(`${API}/api/fdr/attestations/${id}`, {
              method: 'PATCH', credentials: 'include',
              headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ due_date: date }),
            }).catch(() => {})));
            setSelected(new Set()); load();
          }}
          onSendPortal={async () => {
            const results = await Promise.all([...selected].map(id =>
              fetch(`${API}/api/fdr/attestations/${id}/send`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json' }, body: '{}',
              }).then(r => r.json()).catch(() => ({ success: false }))));
            const ok = results.filter(r => r.success).length;
            window.alert(`${ok}/${selected.size} portal invites sent (entities without a compliance officer email are skipped — send those individually).`);
            setSelected(new Set()); load();
          }} />
      )}
    </div>
  );
}

/** Floating bottom-center bulk toolbar — the Asana multi-select pattern. */
function BulkBar({ count, onClear, onSetDue, onSendPortal }) {
  const [due, setDue] = useState('');
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4">
      <span className="text-sm font-bold">{count} selected</span>
      <span className="flex items-center gap-1.5">
        <input type="date" value={due} onChange={e => setDue(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white" />
        <button disabled={!due} onClick={() => onSetDue(due)}
          className="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-slate-700 hover:bg-slate-600 disabled:opacity-40 transition-colors">Set due date</button>
      </span>
      <button onClick={onSendPortal}
        className="px-2.5 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow transition-all">
        Send for e-signature
      </button>
      <button onClick={onClear} className="text-slate-400 hover:text-white text-xs">✕</button>
    </div>
  );
}

/** Inline work surface per attestation — status advance, attester record, element flags, review. */
function AttestationWorkPanel({ attestation: a, onPatch }) {
  const [attester, setAttester] = useState({
    attested_by_name: a.attested_by_name || '', attested_by_title: a.attested_by_title || '',
    attested_by_email: a.attested_by_email || '',
  });
  const [reviewNotes, setReviewNotes] = useState(a.reviewer_notes || '');

  const flagsEditable = ['received', 'under_review'].includes(a.status);

  return (
    <div className="px-11 pb-5 bg-slate-50/50 space-y-4" onClick={e => e.stopPropagation()}>
      {/* Status ladder */}
      <div className="flex items-center gap-2 flex-wrap pt-1">
        {a.status === 'pending' && (
          <>
            <SendViaPortalButton attestation={a} onSent={() => onPatch({})} />
            <button onClick={() => onPatch({ status: 'sent', sent_at: new Date().toISOString() })}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 bg-slate-100 hover:bg-slate-200 transition-colors">
              Mark Sent (manual)
            </button>
          </>
        )}
        {['sent', 'reminder_sent'].includes(a.status) && (
          <SendViaPortalButton attestation={a} onSent={() => onPatch({})} resend />
        )}
        {['sent', 'reminder_sent'].includes(a.status) && (
          <>
            <button onClick={() => onPatch({ status: 'reminder_sent', reminder_sent_at: new Date().toISOString() })}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors">
              Log Reminder
            </button>
            <button onClick={() => onPatch({ status: 'received', received_at: new Date().toISOString() })}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-700 transition-colors">
              Mark Received
            </button>
          </>
        )}
        {a.status === 'received' && (
          <button onClick={() => onPatch({ status: 'under_review' })}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-slate-700 transition-colors">
            Begin Review
          </button>
        )}
        {a.status === 'under_review' && (
          <>
            <button onClick={() => onPatch({ status: 'accepted', reviewed_at: new Date().toISOString(), reviewer_notes: reviewNotes || null })}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-500 transition-colors">
              Accept
            </button>
            <button onClick={() => onPatch({ status: 'rejected', reviewed_at: new Date().toISOString(), reviewer_notes: reviewNotes || null })}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-500 transition-colors">
              Reject
            </button>
            <input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)} placeholder="Reviewer notes"
              className="flex-1 min-w-[220px] border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
          </>
        )}
        {['accepted', 'rejected'].includes(a.status) && (
          <span className="text-xs text-slate-500">
            {a.status === 'accepted' ? 'Accepted' : 'Rejected'}{a.reviewer_notes ? ` — ${a.reviewer_notes}` : ''}.
            {a.status === 'rejected' && ' Unconfirmed elements should be logged as findings.'}
          </span>
        )}
      </div>

      {/* Attester record — capture on receipt */}
      {flagsEditable && (
        <div className="grid grid-cols-3 gap-2.5">
          {[['attested_by_name', 'Attester name'], ['attested_by_title', 'Title (authorized representative)'], ['attested_by_email', 'Email']].map(([k, ph]) => (
            <input key={k} value={attester[k]} placeholder={ph}
              onChange={e => setAttester(s => ({ ...s, [k]: e.target.value }))}
              onBlur={() => attester[k] !== (a[k] || '') && onPatch({ [k]: attester[k] || null, attested_at: a.attested_at || new Date().toISOString() })}
              className="border border-slate-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500/20" />
          ))}
        </div>
      )}

      {/* The signed record: the exact question set + typed answers when the
          vendor signed through the portal; the nine legacy toggles otherwise. */}
      <SignedAnswers attestation={a} flagsEditable={flagsEditable} onPatch={onPatch} />
    </div>
  );
}

function SignedAnswers({ attestation: a, flagsEditable, onPatch }) {
  const [record, setRecord] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/fdr/attestations/${a.id}/signature`, { credentials: 'include' })
      .then(r => r.json()).then(d => { if (alive && d.success) setRecord(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [a.id]);

  const sig = record?.signature;
  const questions = record?.question_set;

  if (sig && Array.isArray(questions)) {
    const answers = sig.elements || {};
    const fmt = (q) => {
      const v = answers[q.qkey];
      if (v === undefined || v === null || v === '') return '—';
      if (q.response_type === 'yes_no') return v === 'yes' ? 'Confirmed' : 'Cannot confirm';
      if (Array.isArray(v)) return v.join(', ');
      return String(v);
    };
    return (
      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">
          Signed answers — {sig.signer_name} ({sig.signer_title}) · {String(sig.signed_at).split('T')[0]} · text {sig.text_version}
        </div>
        <div className="space-y-1">
          {questions.map((q, i) => {
            const v = answers[q.qkey];
            const bad = q.response_type === 'yes_no' ? v !== 'yes'
              : q.requires_explanation_on && String(v) === String(q.requires_explanation_on);
            return (
              <div key={q.qkey} className="flex items-start justify-between gap-3 text-xs bg-white border border-slate-100 rounded-lg px-3 py-1.5">
                <span className="text-slate-600 min-w-0">{i + 1}. {q.label}</span>
                <span className={`font-semibold flex-shrink-0 ${bad ? 'text-amber-600' : 'text-emerald-600'}`}>{fmt(q)}</span>
              </div>
            );
          })}
        </div>
        {sig.exceptions_note && (
          <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <span className="font-bold">Documented exception:</span> {sig.exceptions_note}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1.5">
        Element confirmations {flagsEditable ? '— set exactly what the entity confirmed' : ''}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {ELEMENTS.map(e => (
          <button key={e.key} disabled={!flagsEditable}
            onClick={() => onPatch({ [e.key]: !a[e.key] })}
            className={`px-2.5 py-1 rounded-lg text-[11px] font-bold transition-colors disabled:cursor-default ${
              a[e.key] ? 'bg-emerald-100 text-emerald-700' : flagsEditable ? 'bg-white border border-slate-200 text-slate-500 hover:bg-slate-100' : 'bg-slate-100 text-slate-400'
            }`}>
            {e.label} {a[e.key] ? '✓' : '—'}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Launch the year's campaign — creates an attestation for every active entity missing one. */
function LaunchCampaignModal({ year, existing, onClose, onDone }) {
  const [entities, setEntities] = useState([]);
  const [dueDate, setDueDate] = useState(`${year}-12-31`);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API}/api/fdr/entities`, { credentials: 'include' }).then(r => r.json())
      .then(d => { if (d.success) setEntities((d.entities || []).filter(e => ['active', 'probation'].includes(e.status))); })
      .catch(() => {});
  }, []);

  const already = new Set(existing.map(a => a.entity_id));
  const targets = entities.filter(e => !already.has(e.id));

  const launch = async () => {
    setRunning(true); setError('');
    let created = 0, failed = 0;
    for (const e of targets) {
      try {
        const res = await fetch(`${API}/api/fdr/attestations`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            entity_id: e.id, attestation_year: year,
            period_start: `${year}-01-01`, period_end: `${year}-12-31`, due_date: dueDate,
          }),
        });
        const data = await res.json();
        if (data.success) created++; else failed++;
      } catch { failed++; }
    }
    setResult({ created, failed });
    setRunning(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8" onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-slate-800">Launch {year} Attestation Campaign</h3>
        {result ? (
          <>
            <p className="text-sm text-slate-600 mt-3">
              Created <strong>{result.created}</strong> attestation record{result.created !== 1 ? 's' : ''}.
              {result.failed > 0 && <span className="text-red-600"> {result.failed} failed.</span>}
            </p>
            <div className="flex justify-end mt-6">
              <button onClick={onDone} className={btnPrimary}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-500 mt-2">
              One record per entity per year, for every active or probation entity without one.
              {targets.length === 0
                ? ' All eligible entities already have a record for this year.'
                : ` ${targets.length} entit${targets.length === 1 ? 'y' : 'ies'} will get a record: ${targets.slice(0, 5).map(t => t.name).join(', ')}${targets.length > 5 ? '…' : ''}.`}
            </p>
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-1">Due date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={inputCls} />
            </div>
            {error && <div className="text-red-600 text-sm bg-red-50 rounded-xl px-4 py-2 mt-3">{error}</div>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">Cancel</button>
              <button onClick={launch} disabled={running || targets.length === 0} className={btnPrimary}>
                {running ? 'Creating…' : `Create ${targets.length} Record${targets.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** Send the attestation for e-signature through the vendor portal (Phase 2).
 *  Uses the entity's compliance officer email by default; prompts if absent. */
function SendViaPortalButton({ attestation, onSent, resend = false }) {
  const [open, setOpen] = React.useState(false);
  const [note, setNote] = React.useState(null);

  return (
    <span className="inline-flex items-center gap-2">
      <button onClick={() => setOpen(true)}
        className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-sm hover:shadow transition-all">
        {resend ? 'Resend Portal Invite' : 'Send for e-Signature'}
      </button>
      {note && <span className={`text-[11px] font-medium ${note.ok ? 'text-emerald-600' : 'text-red-600'}`}>{note.text}</span>}
      {open && (
        <SendInviteModal attestation={attestation}
          onClose={() => setOpen(false)}
          onDone={(msg) => { setOpen(false); setNote({ ok: true, text: msg }); onSent?.(); }} />
      )}
    </span>
  );
}

/**
 * The send sheet: who signs, and which of YOUR compliance users ride along
 * in CC. CC choices come from the org's own user list and are resolved by id
 * server-side — free-typed external CC addresses are deliberately impossible.
 */
function SendInviteModal({ attestation, onClose, onDone }) {
  const [email, setEmail] = React.useState('');
  const [name, setName] = React.useState('');
  const [members, setMembers] = React.useState([]);
  const [cc, setCc] = React.useState(new Set());
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  const [prefilled, setPrefilled] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    fetch(`${API}/api/fdr/entities/${attestation.entity_id}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        if (!alive || !d.success) return;
        const e = d.entity || d;
        if (e.compliance_officer_email) { setEmail(e.compliance_officer_email); setName(e.compliance_officer_name || ''); }
        setPrefilled(true);
      }).catch(() => setPrefilled(true));
    fetch(`${API}/api/audit/team-members`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { if (alive && d.success) setMembers(d.members || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [attestation.entity_id]);

  const send = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/fdr/attestations/${attestation.id}/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contact_email: email.trim(), contact_name: name.trim() || undefined,
          cc_user_ids: [...cc],
        }),
      }).then(r => r.json());
      if (!r.success) throw new Error(r.message || 'Send failed');
      onDone(`Invite emailed to ${r.sent_to}${r.cc?.length ? ` · CC ${r.cc.length}` : ''}`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-base font-bold text-slate-800">Send for e-signature</h3>
          <p className="text-xs text-slate-400 mt-0.5">{attestation.entity_name} · CY{attestation.attestation_year}. The invite carries your logo and names the vendor.</p>
        </div>
        <div className="px-6 py-4 space-y-3">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Vendor signer email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder={prefilled ? 'name@vendor.com' : 'Loading…'}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
          </div>
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Signer name (optional)</label>
            <input value={name} onChange={e => setName(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-400" />
          </div>
          {members.length > 0 && (
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">CC your compliance team</label>
              <div className="max-h-32 overflow-auto space-y-1 border border-slate-100 rounded-xl p-2">
                {members.map(m => (
                  <label key={m.id} className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer py-0.5">
                    <input type="checkbox" checked={cc.has(m.id)}
                      onChange={() => setCc(prev => { const n = new Set(prev); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; })}
                      className="rounded border-slate-300 text-orange-600" />
                    {m.name || m.email}
                  </label>
                ))}
              </div>
            </div>
          )}
          {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-xl">Cancel</button>
          <button onClick={send} disabled={busy || !/\S+@\S+\.\S+/.test(email)}
            className="px-5 py-2 text-sm font-semibold rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
            {busy ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
